import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';
import { SWORN_DATA } from '../../gameData';

interface TutorialOverlayProps {
  onClose: () => void;
}

const TutorialOverlay: React.FC<TutorialOverlayProps> = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  // Use a more aggressive check for "mobile" layout to handle landscape phones and tablets
  // where using absolute positioning based on percentage often clips off-screen.
  const checkMobile = () => window.innerWidth < 1280 || window.innerHeight < 800;
  
  const [isMobile, setIsMobile] = useState(checkMobile());

  useEffect(() => {
    const handleResize = () => setIsMobile(checkMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const steps = SWORN_DATA.tutorial_steps || [];
  const assets = SWORN_DATA.ui_assets?.tutorial;

  if (!steps.length || !assets) return null;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const step = steps[currentStep];

  const getMobileContent = (index: number, content: string) => {
      if (index === 0) return "Tap the portrait at the top left to select your Knight.\nEach has unique weapons and spells.";
      if (index === 1) return "Below that is your weapon and spell loadout.\nTap the icons to change them.";
      if (index === 2) return "Tap the center title bar to Name, Save, Load, or Share your build with others.";
      if (index === 3) return "Tap any Fae icon to open the Compendium. Here you can browse, filter, search and add blessings to your optimal build.";
      if (index === 4) return "Tap the knight portraits on the right to view detailed stats and information including base stats and growth.";
      if (index === 5) return "Access the Anvil, Merlin, Morgana, Nimue forge, and Source panels in the middle to select modifiers for the build.";
      return content.replace(/Click/g, "Tap");
  };

  const displayContent = isMobile ? getMobileContent(currentStep, step.content) : step.content;

  return (
    <div className="fixed inset-0 z-[2000] bg-black/60 transition-opacity duration-300 pointer-events-auto flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
      <div 
        className={`transition-all duration-300 flex flex-col ${
            isMobile 
            ? 'relative w-[90%] max-w-sm mx-auto' 
            : 'absolute w-64 md:w-80'
        }`}
        style={!isMobile ? (step.position as React.CSSProperties) : {}}
      >
        {/* Arrow */}
        {!isMobile && (
          <>
            <img 
                src={assets.arrow} 
                alt=""
                className="absolute w-16 h-16 z-20 pointer-events-none"
                style={step.arrowStyle as React.CSSProperties}
            />

            {/* Extra Arrows for Panels Step */}
            {step.extraArrows && step.extraArrows.map((style, idx) => (
                <img 
                    key={idx}
                    src={assets.arrow} 
                    alt=""
                    className="absolute w-16 h-16 z-20 pointer-events-none"
                    style={style as React.CSSProperties}
                />
            ))}
          </>
        )}

        {/* Title Card */}
        <div 
            className="w-full h-12 flex items-center justify-between px-4 z-10 relative"
            style={{
                backgroundImage: `url(${assets.title_card})`,
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat'
            }}
        >
          <h3 className="font-bold text-sworn-gold text-lg text-shadow-sm truncate pr-2">{step.title}</h3>
          <button onClick={onClose} className="text-slate-300 hover:text-white transition-colors" aria-label="Close Tutorial">
            <X size={18} />
          </button>
        </div>

        {/* Content Frame */}
        <div 
            className="w-full px-5 py-4 -mt-1 relative z-0 text-white"
            style={{
                backgroundImage: `url(${assets.frame})`,
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat',
                minHeight: '130px'
            }}
        >
            <p className="text-sm text-slate-300 font-medium leading-relaxed drop-shadow-md mb-4 whitespace-pre-line">
            {displayContent}
            </p>

            <div className="flex justify-between items-center mt-2">
            <div className="flex gap-1">
                {steps.map((_, idx) => (
                <div 
                    key={idx} 
                    className={`w-1.5 h-1.5 rounded-full shadow-sm ${idx === currentStep ? 'bg-sworn-gold ring-1 ring-sworn-gold/50' : 'bg-slate-600/80'}`}
                />
                ))}
            </div>
            
            <div className="flex gap-2">
                <button 
                onClick={handlePrev}
                disabled={currentStep === 0}
                className={`p-1 rounded hover:bg-black/20 text-sworn-gold transition-all ${currentStep === 0 ? 'opacity-0 cursor-default' : 'opacity-100'}`}                aria-label="Previous Step"                >
                <ChevronLeft size={20} />
                </button>
                <button 
                onClick={handleNext}
                className="flex items-center gap-1 px-3 py-1 bg-sworn-gold hover:bg-sworn-gold-light text-black font-bold rounded shadow-lg text-sm transition-all transform hover:scale-105"
                >
                {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
                {currentStep < steps.length - 1 && <ChevronRight size={16} />}
                </button>
            </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialOverlay;
